import { QuestionEntity, QuestionTypeAllItems } from '../../../../meta-data/app-meta-data.service';
import {
  AiSource,
  DraftAnswer,
  DraftCoverage,
  DraftQuestion,
  DraftQuestionType,
  DraftQuiz,
  QuestionLinks,
  QuestionVersion,
  VariantRequest,
  VersionHint
} from '../../models/quiz-builder.model';
import { CoverageQuestionVm } from '../../models/quiz-video-coverage.model';

export const DRAFT_STORAGE_KEY = 'aviator.prototype.quizBuilder.draft';
// ponytail: local dev proxy only (keeps AI keys off the browser); a real API endpoint replaces it if this ships.
export const AI_PROXY = 'http://127.0.0.1:4310';
const PROXY_DOWN = 'AI proxy isn’t running. Start it with: node scripts/quiz-builder-ai-proxy.js';

export function aiSource(model: string): AiSource {
  const id = model || '';
  if (/jev/i.test(id)) {
    return { kind: 'jev', label: 'TypeSafe Jev', model: id };
  }
  if (/claude/i.test(id)) {
    return { kind: 'claude', label: 'Claude', model: id };
  }
  return { kind: 'luna', label: 'AI', model: id || 'gpt-6-luna' };
}

export function proxyError(err: { error?: { error?: unknown } }): string {
  return err && err.error && typeof err.error.error === 'string' ? err.error.error : PROXY_DOWN;
}

export const VERSION_HINTS: { key: VersionHint; label: string }[] = [
  { key: 'clearer', label: 'Clearer' },
  { key: 'harder', label: 'Harder' },
  { key: 'match-video', label: 'Match video' }
];

export const QUESTION_TYPES: { key: DraftQuestionType; label: string }[] = [
  { key: 'MC', label: 'Multiple choice' },
  { key: 'MA', label: 'Multiple answer' },
  { key: 'TF', label: 'True / False' }
];

/** Template answers for a new question: True/False is fixed, choice types start with four blanks. */
export function blankAnswers(type: DraftQuestionType): DraftAnswer[] {
  return type === 'TF'
    ? [{ text: 'True', correct: false }, { text: 'False', correct: false }]
    : [1, 2, 3, 4].map(() => ({ text: '', correct: false }));
}

/** Same rules the AI proxy applies to model output; '' means the question is ready. */
export function questionProblem(type: DraftQuestionType, question: string, answers: DraftAnswer[]): string {
  const correct = answers.filter(a => a.correct).length;
  if (!question.trim()) {
    return 'Write the question.';
  }
  if (answers.length < 2 || answers.length > 6) {
    return 'Use 2 to 6 answers.';
  }
  if (answers.some(a => !a.text.trim())) {
    return 'Every answer needs text.';
  }
  if (type === 'MA') {
    return correct ? '' : 'Mark at least one correct answer.';
  }
  return correct === 1 ? '' : 'Mark the correct answer.';
}

/** Marks answer `index` correct: exclusive for single-answer types, toggled for multiple answer. */
export function toggleCorrect(type: DraftQuestionType, answers: DraftAnswer[], index: number): DraftAnswer[] {
  return answers.map((a, i) => type === 'MA'
    ? (i === index ? { ...a, correct: !a.correct } : a)
    : { ...a, correct: i === index });
}

// ponytail: negative ids keep new questions apart from bank QuestionIds without a second key field.
export function newQuestionId(draft: DraftQuiz): number {
  return Math.min(0, ...draft.questions.map(q => q.id)) - 1;
}

/** Adds a question, or replaces the one with the same id in place (re-editing a new question). */
export function upsertQuestion(draft: DraftQuiz, question: DraftQuestion): DraftQuiz {
  return draft.questions.some(q => q.id === question.id)
    ? touch(draft, draft.questions.map(q => q.id === question.id ? question : q))
    : touch(draft, [...draft.questions, question]);
}

export type QuestionEdit = Pick<DraftQuestion, 'id' | 'type' | 'question' | 'answers' | 'explanation' | 'links' | 'ai'>;

/** Saves editor fields onto a question. Bank questions keep their coverage, and their bank wording once so a hand edit can be restored. */
export function applyEdit(existing: DraftQuestion | undefined, edit: QuestionEdit): DraftQuestion {
  if (!existing || existing.isNew) {
    return { ...existing, ...edit, coverage: null, isNew: true };
  }
  const changed = existing.question !== edit.question || JSON.stringify(existing.answers) !== JSON.stringify(edit.answers);
  return changed
    ? { ...existing, ...edit, versionReason: existing.versionReason || 'Edited by hand', original: existing.original || { question: existing.question, answers: existing.answers } }
    : { ...existing, ...edit };
}

export function emptyDraft(): DraftQuiz {
  return { name: 'Untitled quiz', questions: [], updatedAt: new Date().toISOString() };
}

/** Restores a saved draft; anything unreadable starts a fresh one rather than breaking the page. */
export function parseDraft(json: string | null): DraftQuiz {
  try {
    const draft = JSON.parse(json || '');
    return draft && Array.isArray(draft.questions) ? draft : emptyDraft();
  } catch {
    return emptyDraft();
  }
}

export function coverageSnapshot(vm: CoverageQuestionVm): DraftCoverage {
  const home = vm.matches.find(m => m.isHome);
  const match = vm.status === 'covered' && home ? home : vm.matches[0];
  return {
    status: vm.status,
    statusLabel: vm.statusLabel,
    where: vm.bestLabel,
    excerpt: (vm.wording && vm.wording.excerpt) || (match && match.excerpt) || '',
    lessonId: match ? match.lessonId : undefined,
    moduleId: vm.moduleId
  };
}

export function toDraftQuestion(entity: QuestionEntity, coverage: DraftCoverage | null): DraftQuestion {
  const typeId = entity.QuestionTypeId;
  const type = typeId === QuestionTypeAllItems.TrueOrFalse ? 'TF' : typeId === QuestionTypeAllItems.MultipleAnswer ? 'MA' : 'MC';
  const isTrue = (entity.CorrectAnswer || '').toLowerCase() === 'true';
  const answers = type === 'TF'
    ? [{ text: 'True', correct: isTrue }, { text: 'False', correct: !isTrue }]
    : (entity.QuestionAnswer || []).map(a => ({ text: stripHtml(a.AnswerText), correct: !!a.IsCorrectAnswer, why: stripHtml(a.ExplanationText) }));
  return {
    id: entity.QuestionId as number,
    type,
    question: stripHtml(entity.Question) || entity.QuestionName || 'Untitled question',
    answers,
    coverage
  };
}

export function addQuestion(draft: DraftQuiz, question: DraftQuestion): DraftQuiz {
  return draft.questions.some(q => q.id === question.id) ? draft : touch(draft, [...draft.questions, question]);
}

export function removeQuestion(draft: DraftQuiz, id: number): DraftQuiz {
  return touch(draft, draft.questions.filter(q => q.id !== id));
}

/** Swaps in an AI version, keeping the bank wording once so it can be restored. */
export function applyVersion(draft: DraftQuiz, id: number, version: QuestionVersion): DraftQuiz {
  return touch(draft, draft.questions.map(q => q.id !== id ? q : {
    ...q,
    question: version.question,
    answers: version.answers,
    versionReason: version.reason,
    ai: version.ai,
    original: q.original || { question: q.question, answers: q.answers }
  }));
}

export function revertQuestion(draft: DraftQuiz, id: number): DraftQuiz {
  return touch(draft, draft.questions.map(q => {
    if (q.id !== id || !q.original) {
      return q;
    }
    const { original, versionReason, ai, ...rest } = q;
    return { ...rest, question: original.question, answers: original.answers };
  }));
}

export function setQuestionLinks(draft: DraftQuiz, id: number, links: QuestionLinks): DraftQuiz {
  return touch(draft, draft.questions.map(q => q.id === id ? { ...q, links } : q));
}

export function buildVariantRequest(question: DraftQuestion, hint: VersionHint | null, count = 3): VariantRequest {
  return {
    type: question.type,
    question: question.question,
    answers: question.answers.map(a => ({ text: a.text, correct: a.correct })),
    coverage: question.coverage,
    hint,
    count
  };
}

function touch(draft: DraftQuiz, questions: DraftQuestion[]): DraftQuiz {
  return { ...draft, questions, updatedAt: new Date().toISOString() };
}

function stripHtml(html: string | undefined): string {
  if (!html) {
    return '';
  }
  // DOMParser builds an inert document: no image loads or handlers fire from bank HTML.
  const text = new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
  return text.replace(/\s+/g, ' ').trim();
}
